import axios from 'axios';
/**
 * Shared API Client for all restaurant projects
 * Handles common operations like authentication, menu, orders
 */
export class RestaurantApiClient {
    constructor(config) {
        Object.defineProperty(this, "client", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "publicClient", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "projectConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.projectConfig = config;
        // Main API client for authenticated routes (/api/*)
        this.client = axios.create({
            baseURL: config.apiBaseUrl,
            timeout: 10000,
            withCredentials: Boolean(config.withCredentials),
            headers: {
                'Content-Type': 'application/json',
                'X-Project-Name': config.projectIdentifier,
                ...config.customHeaders
            }
        });
        // Public API client for public routes (/public/*)
        const publicBaseUrl = config.publicApiBaseUrl || config.apiBaseUrl.replace('/api', '/public');
        console.log('🔧 RestaurantApiClient Constructor Debug [UPDATED]:');
        console.log('  - Config API Base URL:', config.apiBaseUrl);
        console.log('  - Config Public API Base URL:', config.publicApiBaseUrl);
        console.log('  - Final Public Base URL:', publicBaseUrl);
        console.log('  - Project Identifier:', config.projectIdentifier);
        console.log('  - Type of publicApiBaseUrl:', typeof config.publicApiBaseUrl);
        console.log('  - Config object keys:', Object.keys(config));
        this.publicClient = axios.create({
            baseURL: publicBaseUrl,
            // Grosse Menu-Bundles (80+ Produkte) koennen >10s dauern
            timeout: 30000,
            withCredentials: Boolean(config.withCredentials),
            headers: {
                'X-Project-Name': config.projectIdentifier,
                ...config.customHeaders
                // Kein Default Content-Type — vermeidet unnoetige CORS-Preflights bei GET
            }
        });
        // Verify that the baseURL is correctly set
        console.log('✅ Public Client Base URL after creation:', this.publicClient.defaults.baseURL);
        console.log('✅ Main Client Base URL after creation:', this.client.defaults.baseURL);
        this.setupInterceptors();
    }
    setupInterceptors() {
        // Setup interceptors for main API client (authenticated routes)
        this.setupClientInterceptors(this.client, 'API');
        // Setup interceptors for public API client (public routes)
        this.setupClientInterceptors(this.publicClient, 'Public API');
    }
    setupClientInterceptors(client, clientType) {
        // Request interceptor
        client.interceptors.request.use((config) => {
            console.log(`🔄 ${clientType} Request [${this.projectConfig.name}]: ${config.method?.toUpperCase()} ${config.url}`);
            // Add project context to request body for POST/PUT requests
            if (config.data && typeof config.data === 'object') {
                config.data._projectContext = {
                    name: this.projectConfig.projectIdentifier,
                    timestamp: new Date().toISOString()
                };
            }
            return config;
        }, (error) => {
            console.error(`❌ ${clientType} Request Error:`, error);
            return Promise.reject(error);
        });
        // Response interceptor
        client.interceptors.response.use((response) => {
            console.log(`✅ ${clientType} Response [${this.projectConfig.name}]: ${response.status} ${response.config.url}`);
            return response;
        }, (error) => {
            console.error(`❌ ${clientType} Error [${this.projectConfig.name}]:`, error.response?.status, error.message);
            if (error.response?.status === 404) {
                console.warn(`🔍 ${clientType} Endpoint not found - check if server is running`);
            }
            if (error.code === 'ECONNREFUSED') {
                console.warn('⚠️ Cannot connect to Admin Panel - is it running?');
            }
            return Promise.reject(error);
        });
    }
    // Common API Methods that all restaurants need
    /**
     * System Health Check
     */
    async getHealth() {
        try {
            const response = await this.client.get('/system/health');
            return {
                status: 'success',
                data: response.data,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get Full Menu Bundle (products, categories, ingredients - all cached)
     */
    async getMenuBundle() {
        try {
            console.log('🔍 getMenuBundle() Debug:', {
                publicClientBaseURL: this.publicClient.defaults.baseURL,
                publicClientTimeout: this.publicClient.defaults.timeout,
                publicClientHeaders: this.publicClient.defaults.headers,
                endpoint: '/products',
                fullUrl: `${this.publicClient.defaults.baseURL}/products`
            });
            const response = await this.publicClient.get('/products');
            const responseData = response.data.data || {};
            // Debug: Log the structure to understand what we're receiving
            console.log('🔍 API Response structure:', {
                hasData: !!response.data.data,
                hasCategories: !!(responseData.categories),
                categoriesLength: responseData.categories?.length || 0,
                categoriesPreview: responseData.categories?.slice(0, 3).map((cat) => ({
                    name: cat.name,
                    sortOrder: cat.sortOrder
                })) || []
            });
            return {
                status: 'success',
                data: responseData,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    async getProductSlider(params = {}) {
        try {
            const response = await this.publicClient.get('/products/slider', {
                params: {
                    type: params.type,
                    limit: params.limit,
                    categoryId: params.categoryId,
                    categorySlug: params.categorySlug,
                    tag: params.tag,
                    includeUnavailable: params.includeUnavailable ? 'true' : undefined,
                    popularDays: params.popularDays
                }
            });
            const payload = response.data?.data;
            const items = Array.isArray(payload?.items) ? payload.items : [];
            const cacheInfo = payload?.cache ?? response.data?.cache;
            const meta = payload?.meta
                ? {
                    ...payload.meta,
                    cacheHit: payload.meta.cacheHit ?? cacheInfo?.hit
                }
                : {
                    type: params.type || 'default',
                    limit: params.limit ?? (items.length || 0),
                    total: items.length,
                    filtered: items.length,
                    available: items.length,
                    tag: params.tag,
                    cacheHit: cacheInfo?.hit
                };
            return {
                status: 'success',
                data: {
                    items,
                    meta,
                    cache: cacheInfo
                },
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get Menu Items (cached for better performance)
     */
    async getMenu() {
        try {
            const bundleResponse = await this.getMenuBundle();
            if (bundleResponse.status === 'success' && bundleResponse.data.products) {
                return {
                    status: 'success',
                    data: bundleResponse.data.products,
                    timestamp: bundleResponse.timestamp
                };
            }
            return {
                status: 'error',
                message: 'No products found in bundle',
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get Categories (from cached bundle)
     */
    async getCategories() {
        try {
            const bundleResponse = await this.getMenuBundle();
            if (bundleResponse.status === 'success' && bundleResponse.data.categories) {
                return {
                    status: 'success',
                    data: bundleResponse.data.categories,
                    timestamp: bundleResponse.timestamp
                };
            }
            return {
                status: 'error',
                message: 'No categories found in bundle',
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get Ingredients (from cached bundle)
     */
    async getIngredients() {
        try {
            const bundleResponse = await this.getMenuBundle();
            if (bundleResponse.status === 'success' && bundleResponse.data.ingredients) {
                return {
                    status: 'success',
                    data: bundleResponse.data.ingredients,
                    timestamp: bundleResponse.timestamp
                };
            }
            return {
                status: 'error',
                message: 'No ingredients found in bundle',
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get Ingredient Categories (from cached bundle)
     */
    async getIngredientCategories() {
        try {
            const bundleResponse = await this.getMenuBundle();
            if (bundleResponse.status === 'success' && bundleResponse.data.ingredientCategories) {
                return {
                    status: 'success',
                    data: bundleResponse.data.ingredientCategories,
                    timestamp: bundleResponse.timestamp
                };
            }
            return {
                status: 'error',
                message: 'No ingredient categories found in bundle',
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get Special Deals (cached for better performance)
     */
    async getSpecialDeals(limit = 8) {
        try {
            const response = await this.client.get(`/products/deals?limit=${limit}`);
            return {
                status: 'success',
                data: response.data.deals || [],
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get Menu Item by ID (cached for better performance)
     */
    async getMenuItem(id) {
        try {
            const response = await this.publicClient.get(`/products/${encodeURIComponent(id)}`);
            const payload = response.data?.data;
            const product = payload?.product ?? payload ?? response.data?.product;
            return {
                status: 'success',
                data: product,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get User Orders
     */
    async getOrders(filters) {
        try {
            let url = '/orders';
            if (filters) {
                const params = new URLSearchParams();
                Object.entries(filters).forEach(([key, value]) => {
                    if (value)
                        params.append(key, value);
                });
                if (params.toString()) {
                    url += `?${params.toString()}`;
                }
            }
            const response = await this.client.get(url);
            return {
                status: 'success',
                data: response.data.orders || [],
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get Active Orders
     */
    async getActiveOrders() {
        try {
            const response = await this.client.get('/orders/active');
            return {
                status: 'success',
                data: response.data.orders || [],
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Create New Order
     */
    async createOrder(orderData) {
        try {
            const response = await this.publicClient.post('/orders', orderData);
            return {
                status: 'success',
                data: response.data, // ✅ FIXED: Use response.data directly, not response.data.order
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Submit Order (alias for createOrder)
     */
    async submitOrder(orderData) {
        return this.createOrder(orderData);
    }
    /**
     * Get Order by ID (Public Route - No Auth Required)
     */
    async getOrder(orderId) {
        try {
            const response = await this.publicClient.get(`/orders/${orderId}`);
            return {
                status: 'success',
                data: response.data.order,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get Order by Order Number (Public Route - No Auth Required)
     */
    async getOrderByNumber(orderNumber) {
        try {
            const response = await this.publicClient.get(`/orders/number/${orderNumber}`);
            return {
                status: 'success',
                data: response.data.order,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get feedback request metadata by token (public route)
     */
    async getFeedbackRequest(uuid) {
        try {
            const response = await this.publicClient.get(`/feedback/${encodeURIComponent(uuid)}`);
            return {
                status: 'success',
                data: response.data?.data || null,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error?.response?.data?.message || error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Submit feedback rating (public route)
     */
    async submitFeedback(uuid, rating, comment) {
        try {
            const response = await this.publicClient.post(`/feedback/${encodeURIComponent(uuid)}`, {
                rating,
                comment
            });
            return {
                status: 'success',
                data: response.data?.data || null,
                message: response.data?.message,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error?.response?.data?.message || error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get Order History (alias for getOrders)
     */
    async getOrderHistory(filters) {
        return this.getOrders(filters);
    }
    /**
     * Get Customer Orders
     */
    async getCustomerOrders(email) {
        try {
            const response = await this.client.get(`/orders/customer/${encodeURIComponent(email)}`);
            return {
                status: 'success',
                data: response.data.orders || [],
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Update Order Status
     */
    async updateOrderStatus(orderId, status, note, updatedBy) {
        try {
            const response = await this.client.put(`/orders/${orderId}/status`, {
                status,
                note,
                updatedBy
            });
            return {
                status: 'success',
                data: response.data.order,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Cancel Order
     */
    async cancelOrder(orderId, reason, cancelledBy) {
        try {
            const response = await this.publicClient.delete(`/orders/${orderId}/cancel`, {
                data: { reason, cancelledBy }
            });
            return {
                status: 'success',
                data: response.data.order,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get Today's Order Statistics
     */
    async getTodaysStats() {
        try {
            const response = await this.client.get('/orders/stats/today');
            return {
                status: 'success',
                data: response.data.stats,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * User Authentication
     */
    async login(credentials) {
        try {
            const response = await this.client.post('/auth/login', credentials);
            return {
                status: 'success',
                data: response.data,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get User Profile
     */
    async getProfile() {
        try {
            const response = await this.client.get('/auth/profile');
            return {
                status: 'success',
                data: response.data.user,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Calculate order pricing and fees (Enhanced with detailed types and fallback)
     */
    async calculateOrderPricing(request) {
        console.log(`🌐 [${this.projectConfig.name}] API calculateOrderPricing START`, {
            url: '/orders/calculate',
            request,
            timestamp: new Date().toISOString()
        });
        try {
            console.log(`🌐 [${this.projectConfig.name}] Making HTTP POST request...`);
            const httpStart = Date.now();
            // Add timeout wrapper
            const timeout = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('API request timeout after 10 seconds')), 10000);
            });
            const apiCall = this.publicClient.post('/orders/calculate', request);
            const response = await Promise.race([apiCall, timeout]);
            const httpEnd = Date.now();
            console.log(`🌐 [${this.projectConfig.name}] HTTP POST completed in ${httpEnd - httpStart}ms`, {
                status: response.status,
                statusText: response.statusText,
                hasData: !!response.data,
                dataKeys: response.data ? Object.keys(response.data) : []
            });
            return response.data;
        }
        catch (error) {
            // Check for specific error codes that should be passed through to the UI
            if (error.response?.status === 400 && error.response?.data?.code) {
                const errorCode = error.response.data.code;
                if (errorCode === 'RESTAURANT_CLOSED') {
                    console.warn(`🚫 [${this.projectConfig.name}] Restaurant is closed`);
                    // Return the error response as-is to preserve restaurant status information
                    return {
                        status: 'error',
                        code: 'RESTAURANT_CLOSED',
                        message: error.response.data.message || 'Das Restaurant ist derzeit geschlossen.',
                        isOpen: false
                    };
                }
                if (errorCode === 'MINIMUM_ORDER_NOT_REACHED') {
                    console.warn(`📊 [${this.projectConfig.name}] Minimum order value not reached:`, {
                        message: error.response.data.message,
                        data: error.response.data.data,
                        fullResponse: error.response.data,
                        errorStatus: error.response.status,
                        errorCode: error.response.data.code
                    });
                    // Return the error response with minimum order details
                    const response = {
                        status: 'error',
                        code: 'MINIMUM_ORDER_NOT_REACHED',
                        message: error.response.data.message || 'Mindestbestellwert nicht erreicht.',
                        isOpen: true,
                        data: error.response.data.data || {
                            currentAmount: 0,
                            minimumRequired: 15,
                            missingAmount: 15
                        } // Ensure data structure exists
                    };
                    console.log(`📋 [${this.projectConfig.name}] Returning minimum order response:`, {
                        response,
                        responseStatus: response.status,
                        responseCode: response.code,
                        responseData: response.data,
                        responseMessage: response.message
                    });
                    return response;
                }
            }
            console.error(`❌ [${this.projectConfig.name}] calculateOrderPricing error:`, error);
            throw error;
        }
    }
    /**
     * ✅ NEW: Calculate single product price (für ProductConfigModal)
     */
    async calculateProductPrice(request) {
        console.log(`🌐 [${this.projectConfig.name}] API calculateProductPrice START`, {
            url: '/products/calculate',
            request,
            timestamp: new Date().toISOString()
        });
        try {
            const response = await this.publicClient.post('/products/calculate', request);
            console.log(`✅ [${this.projectConfig.name}] calculateProductPrice response:`, response.data);
            return response.data;
        }
        catch (error) {
            console.error(`❌ [${this.projectConfig.name}] calculateProductPrice error:`, error);
            throw error;
        }
    }
    /**
     * Fallback pricing calculation when API is unavailable
     * @deprecated - Not used anymore, we always rely on server calculation
     */
    /*
    private calculateFallbackPricing(request: SharedOrderPricingRequest): SharedOrderPricingResponse {
      const subtotal = request.items.reduce((sum: number, item: OrderItemRequest) => sum + ((item.price || 0) * item.quantity), 0);
      const deliveryFee = request.deliveryType === 'delivery' ? (subtotal >= 25 ? 0 : 3.50) : 0;
      const serviceFee = request.deliveryType === 'delivery' ? subtotal * 0.05 : 0; // 5% fallback
      const total = subtotal + deliveryFee + serviceFee;
      
      return {
        status: 'success',
        message: `Fallback pricing calculation for ${this.projectConfig.name} (API unavailable)`,
        project: this.projectConfig.projectIdentifier,
        timestamp: new Date().toISOString(),
        data: {
          items: request.items,
          pricing: {
            subtotal: Math.round(subtotal * 100) / 100,
            deliveryFee: Math.round(deliveryFee * 100) / 100,
            serviceFee: Math.round(serviceFee * 100) / 100,
            taxAmount: Math.round(total * 0.07 * 100) / 100,
            total: Math.round(total * 100) / 100,
            currency: 'EUR'
          },
          deliveryInfo: {
            type: request.deliveryType,
            freeDeliveryThreshold: 25.00,
            estimatedTime: request.deliveryType === 'delivery' ? '25-35 min' : '15-20 min',
            pickupTime: { min: 15, max: 30 }, // Fallback: 15-30 Minuten
            deliveryTime: { min: 30, max: 60 }, // Fallback: 30-60 Minuten
            // Fallback Zeitpunkte (werden vom Backend überschrieben)
            estimatedPickupTime: {
              min: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
              max: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
              display: new Date(Date.now() + 15 * 60 * 1000).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) + ' Uhr'
            },
            estimatedDeliveryTime: {
              min: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
              max: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
              display: new Date(Date.now() + 30 * 60 * 1000).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) + ' Uhr'
            },
            timezone: 'Europe/Berlin' // Fallback Zeitzone
          },
          tipSuggestions: [
            { percentage: 10, amount: Math.round(subtotal * 0.10 * 100) / 100 },
            { percentage: 15, amount: Math.round(subtotal * 0.15 * 100) / 100 },
            { percentage: 20, amount: Math.round(subtotal * 0.20 * 100) / 100 }
          ],
          breakdown: {
            subtotal: `${Math.round(subtotal * 100) / 100} € (inkl. MwSt)`,
            deliveryFee: deliveryFee > 0 ? `${Math.round(deliveryFee * 100) / 100} €` : 'Kostenlos',
            serviceFee: serviceFee > 0 ? `${Math.round(serviceFee * 100) / 100} € (Fallback 5%)` : 'Keine',
            tax: `${Math.round(total * 0.07 * 100) / 100} € (7% MwSt enthalten)`,
            total: `${Math.round(total * 100) / 100} €`
          }
        }
      };
    }
    */
    /**
     * Check if delivery address is within delivery zones
     */
    async checkDeliveryZone(deliveryAddress) {
        console.log(`🌐 [${this.projectConfig.name}] API checkDeliveryZone START`, {
            url: '/check-delivery-zone',
            deliveryAddress,
            timestamp: new Date().toISOString()
        });
        try {
            console.log(`🌐 [${this.projectConfig.name}] Making HTTP POST request to check delivery zone...`);
            const httpStart = Date.now();
            // Use public client for zone checking
            const response = await this.publicClient.post('/check-delivery-zone', {
                address: deliveryAddress
            });
            const httpEnd = Date.now();
            console.log(`🌐 [${this.projectConfig.name}] Delivery zone check completed in ${httpEnd - httpStart}ms`, {
                status: response.status,
                statusText: response.statusText,
                hasData: !!response.data,
                inZone: response.data?.data?.inZone,
                standardDeliveryFeeCent: response.data?.data?.standardDeliveryFeeCent,
                totalDeliveryFeeCent: response.data?.data?.totalDeliveryFeeCent
            });
            return {
                success: response.data.success,
                inZone: response.data.data?.inZone || false,
                zone: response.data.data?.zone,
                deliveryFeeCent: response.data.data?.deliveryFeeCent,
                standardDeliveryFeeCent: response.data.data?.standardDeliveryFeeCent, // ✅ NEU
                totalDeliveryFeeCent: response.data.data?.totalDeliveryFeeCent, // ✅ NEU
                coordinates: response.data.data?.coordinates,
                error: response.data.success ? undefined : response.data.message
            };
        }
        catch (error) {
            console.error(`❌ [${this.projectConfig.name}] checkDeliveryZone failed:`, error);
            return {
                success: false,
                inZone: false,
                error: error.response?.data?.message || error.message || 'Fehler bei der Liefergebiets-Überprüfung'
            };
        }
    }
    /**
     * Get Google Places Autocomplete predictions for address input
     */
    async getPlacePredictions(input, location) {
        console.log(`🌐 [${this.projectConfig.name}] API getPlacePredictions START`, {
            url: '/places/predictions',
            input,
            location,
            timestamp: new Date().toISOString()
        });
        try {
            const requestBody = { input };
            if (location) {
                requestBody.location = location;
            }
            const response = await this.publicClient.post('/places/predictions', requestBody);
            console.log(`🌐 [${this.projectConfig.name}] Place predictions completed`, {
                status: response.status,
                predictionsCount: response.data?.data?.length || 0
            });
            return {
                success: response.data.success,
                predictions: response.data.data || [],
                error: response.data.success ? undefined : response.data.message
            };
        }
        catch (error) {
            console.error(`❌ [${this.projectConfig.name}] getPlacePredictions failed:`, error);
            return {
                success: false,
                predictions: [],
                error: error.response?.data?.message || error.message || 'Fehler beim Laden der Adressvorschläge'
            };
        }
    }
    /**
     * Get Google Places details for selected place
     */
    async getPlaceDetails(placeId) {
        console.log(`🌐 [${this.projectConfig.name}] API getPlaceDetails START`, {
            url: '/places/details',
            placeId,
            timestamp: new Date().toISOString()
        });
        try {
            const response = await this.publicClient.post('/places/details', { placeId });
            console.log(`🌐 [${this.projectConfig.name}] Place details RAW response:`, {
                status: response.status,
                fullResponse: response.data,
                responseKeys: Object.keys(response.data || {}),
                success: response.data?.success,
                hasData: !!response.data?.data,
                dataStructure: response.data?.data ? typeof response.data.data : 'no data',
                isDataObject: response.data?.data && typeof response.data.data === 'object'
            });
            // ✅ Verbesserte Response-Verarbeitung
            const apiSuccess = response.data?.success;
            const placeData = response.data?.data;
            console.log(`🔍 [${this.projectConfig.name}] Parsed place data:`, {
                apiSuccess,
                placeDataType: typeof placeData,
                placeDataKeys: placeData && typeof placeData === 'object' ? Object.keys(placeData) : 'not an object',
                hasAddressComponents: placeData?.address_components ? placeData.address_components.length : 'no address_components'
            });
            return {
                success: apiSuccess,
                place: placeData,
                error: apiSuccess ? undefined : response.data?.message || 'Unknown error'
            };
        }
        catch (error) {
            console.error(`❌ [${this.projectConfig.name}] getPlaceDetails failed:`, error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Fehler beim Laden der Adressdetails'
            };
        }
    }
    /**
     * Validate address and check delivery zone in one call
     */
    async validateAddress(addressData) {
        console.log(`🌐 [${this.projectConfig.name}] API validateAddress START`, {
            url: '/places/validate',
            addressData,
            timestamp: new Date().toISOString()
        });
        try {
            const response = await this.publicClient.post('/places/validate', addressData);
            console.log(`🌐 [${this.projectConfig.name}] Address validation completed`, {
                status: response.status,
                inZone: response.data?.data?.inZone
            });
            return {
                success: response.data.success,
                inZone: response.data.data?.inZone || false,
                zone: response.data.data?.zone,
                deliveryFeeCent: response.data.data?.deliveryFeeCent,
                coordinates: response.data.data?.coordinates,
                error: response.data.success ? undefined : response.data.message
            };
        }
        catch (error) {
            console.error(`❌ [${this.projectConfig.name}] validateAddress failed:`, error);
            return {
                success: false,
                inZone: false,
                error: error.response?.data?.message || error.message || 'Fehler bei der Adressvalidierung'
            };
        }
    }
    /**
     * Custom API call for project-specific endpoints
     */
    async customCall(method, endpoint, data) {
        try {
            const response = await this.client.request({
                method,
                url: endpoint,
                data
            });
            return {
                status: 'success',
                data: response.data,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            return {
                status: 'error',
                message: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * ✅ PayPal: Create Order with full cart data
     * Sends complete order information (same structure as /orders endpoint)
     */
    async createPayPalOrderWithCart(orderData) {
        try {
            console.log('💳 [RestaurantApiClient] Creating PayPal order with cart data:', {
                itemCount: orderData.items.length,
                orderType: orderData.orderType
            });
            const response = await this.publicClient.post('/payment/paypal/create-order', orderData);
            console.log('✅ [RestaurantApiClient] PayPal order created:', response.data);
            return response.data;
        }
        catch (error) {
            console.error('❌ PayPal order creation failed:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to create PayPal order');
        }
    }
    /**
     * Stripe: Create PaymentIntent for connected account checkout.
     */
    async createStripePaymentIntentWithCart(orderData) {
        try {
            const response = await this.publicClient.post('/payment/stripe/create-payment-intent', orderData);
            return response.data;
        }
        catch (error) {
            console.error('❌ Stripe PaymentIntent creation failed:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to create Stripe payment intent');
        }
    }
    /**
     * ✅ PayPal: Create Order (Legacy - with pricing calculation)
     * @deprecated Use createPayPalOrderWithCart instead for full order support
     */
    async createPayPalOrder(amount, currency, metadata) {
        try {
            // ✅ ENHANCED: Calculate order pricing first to get tax breakdown
            let orderData = {
                amount,
                currency,
                metadata
            };
            // If we have cart items in metadata, calculate pricing first
            if (metadata?.items && metadata?.deliveryType) {
                try {
                    const pricingRequest = {
                        items: metadata.items,
                        deliveryType: metadata.deliveryType,
                        deliveryAddress: metadata.deliveryAddress
                    };
                    const pricingResponse = await this.calculateOrderPricing(pricingRequest);
                    if (pricingResponse.status === 'success' && pricingResponse.data?.pricing) {
                        const pricing = pricingResponse.data.pricing;
                        // Add tax breakdown for German compliance
                        orderData.taxBreakdown = {
                            regular: pricing.taxAmountCent?.['19'] || 0,
                            reduced: pricing.taxAmountCent?.['7'] || 0,
                            noCharge: pricing.taxAmountCent?.['0'] || 0
                        };
                        // Add pricing breakdown (PURE CENT - no fallbacks)
                        orderData.pricingBreakdown = {
                            subtotalCent: pricing.subtotalCent,
                            deliveryFeeCent: pricing.deliveryFeeCent,
                            serviceFeeCent: pricing.serviceFeeCent,
                            totalCent: pricing.totalCent
                        };
                        // Update amount to calculated total
                        orderData.amount = (pricing.totalCent / 100).toFixed(2);
                        console.log('💰 [RestaurantApiClient] PayPal order with pricing:', {
                            amount: orderData.amount,
                            taxBreakdown: orderData.taxBreakdown,
                            pricingBreakdown: orderData.pricingBreakdown
                        });
                    }
                }
                catch (pricingError) {
                    console.warn('⚠️ [RestaurantApiClient] Failed to calculate pricing, proceeding without breakdown:', pricingError);
                }
            }
            const response = await this.publicClient.post('/payment/paypal/create-order', orderData);
            return response.data;
        }
        catch (error) {
            console.error('❌ PayPal order creation failed:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to create PayPal order');
        }
    }
    /**
     * ✅ PayPal: Capture Order
     */
    async capturePayPalOrder(orderID) {
        try {
            const response = await this.publicClient.post('/payment/paypal/capture-order', {
                orderID
            });
            return response.data;
        }
        catch (error) {
            console.error('❌ PayPal order capture failed:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to capture PayPal payment');
        }
    }
    /**
     * ✅ PayPal: Cancel Order (when user closes PayPal popup)
     */
    async cancelPayPalOrder(orderID) {
        try {
            const response = await this.publicClient.post('/payment/paypal/cancel-order', {
                orderID
            });
            return response.data;
        }
        catch (error) {
            console.error('❌ PayPal order cancellation failed:', error);
            throw new Error(error.response?.data?.message || error.message || 'Failed to cancel PayPal order');
        }
    }
    /**
     * Convert Cart Items to Order Request
     * Helper method to transform cart data into API-compatible order structure
     */
    convertCartToOrderRequest(cartItems, customer, orderType, payment, deliveryInfo, specialRequests, captchaToken, discountCode) {
        // Calculate client-side prices for validation
        const subtotalCent = cartItems.reduce((sum, item) => {
            const itemTotalCent = Math.round(item.totalPrice * 100);
            return sum + itemTotalCent;
        }, 0);
        // Calculate total deposit from cart items
        const totalDepositCent = cartItems.reduce((sum, item) => {
            const deposit = item.deposit;
            if (deposit && deposit.type !== 'none' && deposit.amountCent > 0) {
                return sum + (deposit.amountCent * item.quantity);
            }
            return sum;
        }, 0);
        const totalCent = subtotalCent + totalDepositCent;
        console.log(`💰 [${this.projectConfig.name}] Client-calculated prices:`, {
            items: cartItems.map(item => ({
                name: item.menuItem.name,
                quantity: item.quantity,
                totalPrice: item.totalPrice,
                totalPriceCent: Math.round(item.totalPrice * 100),
                deposit: item.deposit,
                depositTotal: item.deposit && item.deposit.amountCent > 0 ? item.deposit.amountCent * item.quantity : 0
            })),
            subtotalCent,
            totalDepositCent,
            totalCent,
            subtotalEuro: (subtotalCent / 100).toFixed(2),
            depositEuro: (totalDepositCent / 100).toFixed(2),
            totalEuro: (totalCent / 100).toFixed(2)
        });
        return {
            customer: {
                name: customer.name,
                email: customer.email,
                phone: customer.phone,
                address: customer.address
            },
            items: cartItems.map(item => {
                if (item.itemType === 'menu') {
                    return {
                        itemType: 'menu',
                        menuId: item.menuId || item.id,
                        quantity: item.quantity,
                        componentConfigs: (item.componentConfigs || []).map((config) => ({
                            productId: config.productId,
                            selectedSizeId: config.selectedSize?.id || config.selectedSize?._id,
                            extraIngredientIds: (config.extras || []).map((e) => e.id || e._id),
                            removedIngredientIds: (config.removedIngredients || []).map((ingredient) => typeof ingredient === 'object' && ingredient !== null
                                ? ingredient._id || ingredient.id
                                : ingredient),
                            selectedMandatoryIngredients: config.selectedMandatoryIngredients || [],
                            specialInstructions: config.specialInstructions
                        }))
                    };
                }
                return {
                    itemType: 'product',
                    productId: item.menuItem._id || item.menuItem.id,
                    quantity: item.quantity,
                    extraIngredientIds: (item.extras || []).map((extra) => extra._id || extra.id),
                    removedIngredientIds: (item.removedIngredients || []).map((ingredient) => {
                        if (typeof ingredient === 'object' && ingredient !== null) {
                            return ingredient._id || ingredient.id;
                        }
                        console.warn(`⚠️ [${this.projectConfig.name}] removedIngredient is a string instead of object:`, ingredient);
                        return ingredient;
                    }),
                    selectedSizeId: item.selectedSize ? (item.selectedSize._id || item.selectedSize.id) : undefined,
                    selectedMandatoryIngredients: item.selectedMandatoryIngredients || [],
                    specialInstructions: item.specialInstructions
                };
            }),
            orderType,
            deliveryInfo,
            payment,
            specialRequests,
            captchaToken,
            discountCode,
            subtotalCent,
            totalCent,
            _projectContext: {
                name: this.projectConfig.projectIdentifier,
                timestamp: new Date().toISOString()
            }
        };
    }
    /**
     * Validate Order Request
     * Checks if order data is complete and valid before submission
     */
    validateOrderRequest(orderData) {
        const errors = [];
        // Customer validations
        if (!orderData.customer?.name || !orderData.customer?.email) {
            errors.push('Customer name and email are required');
        }
        // Items validations
        if (!orderData.items || orderData.items.length === 0) {
            errors.push('At least one order item is required');
        }
        // Validate each item
        orderData.items.forEach((item, index) => {
            if (!item.productId) {
                errors.push(`Item ${index + 1}: Product ID is required`);
            }
            if (!item.quantity || item.quantity <= 0) {
                errors.push(`Item ${index + 1}: Valid quantity is required`);
            }
        });
        // Order type validation
        if (!['delivery', 'pickup', 'dine_in'].includes(orderData.orderType)) {
            errors.push('Valid order type is required (delivery, pickup, dine_in)');
        }
        // Payment method validation
        if (!['cash', 'card', 'paypal', 'bank_transfer', 'crypto'].includes(orderData.payment.method)) {
            errors.push('Valid payment method is required');
        }
        // Delivery address validation for delivery orders
        if (orderData.orderType === 'delivery') {
            if (!orderData.deliveryInfo?.deliveryAddress?.street ||
                !orderData.deliveryInfo?.deliveryAddress?.city ||
                !orderData.deliveryInfo?.deliveryAddress?.postalCode) {
                errors.push('Delivery address is required for delivery orders');
            }
        }
        return {
            isValid: errors.length === 0,
            errors
        };
    }
    async calculatePriceDebounced(config, debounceMs = 300) {
        const timerId = `${config.productId}-${config.selectedSizeId || 'no-size'}`;
        // Clear previous debounce
        if (RestaurantApiClient.debounceTimers.has(timerId)) {
            clearTimeout(RestaurantApiClient.debounceTimers.get(timerId));
            RestaurantApiClient.debounceTimers.delete(timerId);
        }
        // Create debounced promise
        return new Promise((resolve, reject) => {
            const timer = setTimeout(async () => {
                try {
                    console.log('🎯 [RestaurantApiClient] Calculating price for config:', config);
                    const response = await this.publicClient.post('/products/calculate', {
                        productId: config.productId,
                        productName: config.productName,
                        basePriceCent: config.basePriceCent,
                        quantity: config.quantity,
                        selectedSizeId: config.selectedSizeId,
                        extraIngredients: config.extraIngredients || [],
                        removedIngredients: config.removedIngredients || []
                    });
                    if (response.data.success && response.data.data) {
                        console.log('✅ [RestaurantApiClient] Price calculated:', {
                            totalCent: response.data.data.pricing.lineItemTotalCent,
                            totalEuro: response.data.data.pricing.lineItemTotalCent / 100
                        });
                        resolve(response.data.data);
                    }
                    else {
                        throw new Error('Invalid API response');
                    }
                }
                catch (error) {
                    console.error('❌ [RestaurantApiClient] Failed to calculate price:', error);
                    reject(error);
                }
                finally {
                    RestaurantApiClient.debounceTimers.delete(timerId);
                }
            }, debounceMs);
            RestaurantApiClient.debounceTimers.set(timerId, timer);
        });
    }
    /**
     * Format price in cents to Euro string
     */
    static formatPrice(priceCent) {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(priceCent / 100);
    }
    /**
     * Validate discount code
     * @param code Discount code to validate
     * @param orderTotal Order total amount in Euro
     */
    async validateDiscountCode(code, orderTotal) {
        try {
            console.log('🎟️ [RestaurantApiClient] Validating discount code:', code);
            const response = await this.publicClient.post('/discounts/validate', {
                code: code.toUpperCase().trim(),
                orderTotal: orderTotal
            });
            if (response.data.success && response.data.discount) {
                console.log('✅ [RestaurantApiClient] Discount code valid:', response.data.discount);
                return {
                    success: true,
                    discount: response.data.discount
                };
            }
            else {
                console.warn('❌ [RestaurantApiClient] Discount code invalid:', response.data.message);
                return {
                    success: false,
                    message: response.data.message || 'Ungültiger Rabattcode'
                };
            }
        }
        catch (error) {
            console.error('❌ [RestaurantApiClient] Error validating discount code:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Fehler beim Validieren des Rabattcodes'
            };
        }
    }
    /**
     * Fetch cached Google reviews (requires consent flag)
     */
    async getGoogleReviews(consentGranted = false) {
        try {
            const response = await this.publicClient.get('/reviews', {
                params: {
                    consent: consentGranted ? 'true' : 'false'
                }
            });
            if (response.data?.success === false) {
                return {
                    status: 'error',
                    message: response.data?.message || 'Failed to load Google reviews',
                    timestamp: new Date().toISOString()
                };
            }
            return {
                status: 'success',
                data: response.data?.data,
                message: response.data?.message,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            const message = error.response?.data?.message || error.message || 'Failed to load Google reviews';
            return {
                status: 'error',
                message,
                timestamp: new Date().toISOString()
            };
        }
    }
    async getInstagramFeed(consentGranted = false) {
        try {
            const response = await this.publicClient.get('/integrations/instagram-feed', {
                params: {
                    consent: consentGranted ? 'true' : 'false'
                }
            });
            if (response.data?.success === false) {
                return {
                    status: 'error',
                    message: response.data?.message || 'Failed to load Instagram feed',
                    timestamp: new Date().toISOString()
                };
            }
            return {
                status: 'success',
                data: response.data?.data,
                message: response.data?.message,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            const message = error.response?.data?.message || error.message || 'Failed to load Instagram feed';
            return {
                status: 'error',
                message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Fetch opening hours & delivery schedule snapshot
     */
    async getOpeningHours() {
        try {
            const response = await this.publicClient.get('/opening-hours', {
                params: {
                    _ts: Date.now()
                }
            });
            if (response.data?.success === false) {
                return {
                    status: 'error',
                    message: response.data?.message || 'Failed to load opening hours',
                    timestamp: new Date().toISOString()
                };
            }
            return {
                status: 'success',
                data: response.data?.data,
                message: response.data?.message,
                timestamp: new Date().toISOString()
            };
        }
        catch (error) {
            const message = error.response?.data?.message || error.message || 'Failed to load opening hours';
            return {
                status: 'error',
                message,
                timestamp: new Date().toISOString()
            };
        }
    }
    /**
     * Get the underlying axios instance for advanced usage
     */
    getClient() {
        return this.client;
    }
    /**
     * Expose current project identifier (for caching keys etc.)
     */
    getProjectIdentifier() {
        return this.projectConfig.projectIdentifier;
    }
}
/**
 * Calculate Product Price with Debouncing
 * @param config Product configuration
 * @param debounceMs Debounce delay in milliseconds (default: 300ms)
 */
Object.defineProperty(RestaurantApiClient, "debounceTimers", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: new Map()
});
